import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Bio } from '../../data/constants';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import { BlinkingCursor } from '../../utils/CommonStyles';

// Animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// const typingEffect = keyframes`
//   from { width: 0 }
//   to { width: 100% }
// `;

const FooterContainer = styled.div`
  width: 100%;
  padding: 0;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
  background: linear-gradient(343deg, rgba(10, 13, 19, 1) 0%, rgba(17, 20, 28, 1) 100%);
`;

const FooterWrapper = styled.div`
  width: 100%;
  max-width: 1350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px 2rem;
  color: ${({ theme }) => theme.text_primary};
`;

const TerminalWindow = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-top: -50px;
  border-radius: 0 0 10px 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: visible;
  border: 1px solid #333;
  border-top: none;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(to bottom, transparent 0%, rgba(0, 30, 60, 0.05) 100%),
      repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
    pointer-events: none;
    opacity: 0.3;
    z-index: 0;
    border-radius: 0 0 10px 10px;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const TerminalBody = styled.div`
  padding: 25px 40px;
  font-family: 'Courier New', monospace;
  position: relative;
  z-index: 1;

  @media (min-width: 1400px) {
    padding: 30px 60px;
  }
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #fff;
  display: flex;
  position: relative;
  font-weight: 500;
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.delay || '0s'};

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  animation: ${fadeIn} 0.5s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

const NavLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  margin: 10px 0;
  width: 100%;

  @media (min-width: 768px) {
    gap: 30px;
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const NavLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  font-size: 16px;
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
  padding: 5px 10px;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.primary};
    background: rgba(122, 0, 255, 0.1);
    border-color: ${({ theme }) => theme.primary + '50'};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 4px 8px;
  }
`;

const SocialMediaIcons = styled.div`
  display: flex;
  gap: 15px;
  margin: 15px 0;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;

  @media (min-width: 768px) {
    gap: 25px;
  }
`;

const SocialMediaIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_primary};
  font-size: 1.5rem;
  transition: all 0.2s ease-in-out;
  border: 1px solid ${({ theme }) => theme.text_secondary + '50'};
  border-radius: 50%;
  width: 40px;
  height: 40px;

  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-3px);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  }
`;

const Copyright = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
  margin-top: 20px;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterWrapper>
        <TerminalWindow>
          <ScanLine />
          <TerminalBody>
            <TerminalLine delay="0.1s">cd ~/portfolio/contact</TerminalLine>
            <CommandOutput delay="0.2s">
              <NavLinks>
                <NavLink href="#About">About</NavLink>
                <NavLink href="#Skills">Skills</NavLink>
                <NavLink href="#Experience">Experience</NavLink>
                <NavLink href="#Projects">Projects</NavLink>
                <NavLink href="#Education">Education</NavLink>
                <NavLink href="#Contact">Contact</NavLink>
              </NavLinks>
            </CommandOutput>

            <TerminalLine delay="0.3s">ls -la social</TerminalLine>
            <CommandOutput delay="0.4s">
              <SocialMediaIcons>
                {Bio.github && (
                  <SocialMediaIcon href={Bio.github} target="_blank">
                    <GitHubIcon />
                  </SocialMediaIcon>
                )}
                {Bio.linkedin && (
                  <SocialMediaIcon href={Bio.linkedin} target="_blank">
                    <LinkedInIcon />
                  </SocialMediaIcon>
                )}
                {Bio.twitter && (
                  <SocialMediaIcon href={Bio.twitter} target="_blank">
                    <TwitterIcon />
                  </SocialMediaIcon>
                )}
              </SocialMediaIcons>
            </CommandOutput>

            <TerminalLine delay="0.5s">echo $COPYRIGHT</TerminalLine>
            <CommandOutput delay="0.6s">
              <Copyright>
                &copy; {new Date().getFullYear()} {Bio.name}. All rights reserved.
              </Copyright>
            </CommandOutput>

            <TerminalLine delay="0.7s">
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </TerminalBody>
        </TerminalWindow>
      </FooterWrapper>
    </FooterContainer>
  );
};

export default Footer;
